/**
 * The map ↔ pane link — design §6.2, resolution rules from Amendment 1 §0/§useUnitLink.
 *
 * "Map → pane: with a use case active, a click on a hierarchy unit sets `selectedUnit`
 * (existing), and the pane fetches `GET {uc}/units/{id}`." A click on a boundary
 * (`HierarchyLayers.tsx`) already writes `selectedUnit`; this hook adopts it as `intel.unit`
 * (which is what every `useIntelUnitDetail(useCase, unit)` query in the pane is keyed on —
 * the fetch happens because the query's `enabled` flips true) **and** draws the same `units`
 * highlight layer a pane pick draws — fix round 1: a map click is a selection too, and a
 * choropleth with no highlighted unit under it looked like the click had done nothing. It
 * does **not** request a fit on that path: the reader just looked at that spot on the map,
 * so moving the view out from under their click would be the one thing they did not ask for.
 *
 * **Amendment 1 §0/§G.4**: "every intelligence score … is at country or watershed level
 * only; clicking anywhere on the map resolves to a watershed or a country." A boundary click
 * can land on a region or a commune outline just as easily as on a watershed or a country
 * one (`HierarchyLayers` draws all four rows), and neither of those two carries an
 * intelligence score — so before `selectedUnit` is adopted as `intel.unit`, `resolveUnit`
 * below asks `GET /hierarchy/units/{id}` what kind of unit was actually clicked:
 *
 * * a watershed or a country (`admin`, level 0) passes straight through, unchanged;
 * * anything else (a region or a commune) is resolved to the watershed whose polygon
 *   contains the click point — `lastClickLatLng` (the Leaflet `latlng` `HierarchyLayers`/
 *   `Choropleth` now record alongside `selectedUnit`), or the unit's own centroid as a
 *   fallback for a unit reached some other way (`SearchBox`) — tested against the watershed
 *   features `GET /hierarchy/units?type=watershed` answers (`findContainingFeature`,
 *   `lib/geo.ts`'s ray-casting helper), TanStack-cached like every other `/hierarchy/units`
 *   call so a watershed layer already on the map costs this hook nothing extra;
 * * if no watershed contains the point, the unit's own country (`GET /hierarchy/units?
 *   type=admin&level=0`, matched on `properties.country`) stands in.
 *
 * `selectedUnit` itself is corrected to match (so the map highlight, `#intelUnitPick` and
 * every later click all agree on the same resolved id), not only `intel.unit` — a commune
 * outline highlighted blue while the pane and the choropleth talk about its watershed would
 * be the exact "click did nothing" symptom fix round 1 already fixed once, just one level
 * up.
 *
 * "Pane → map: choosing a unit from the pane sets `selectedUnit`, requests a fit
 * (`fitRequest`) to the unit's bounds and draws a `units` result layer highlighting it."
 * That is `pick`, the half a `<select id="intelUnitPick">` or a future map-independent list
 * calls — never resolved, because every unit a pane list offers is already a watershed or a
 * country. The bounds come from the same one-row `/hierarchy/units/{id}` the map's own
 * `GeographyFocus` fits scopes from (`fetchUnitDetail`, `queryKeys.unit`) — a unit the map
 * has already looked at costs no request.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { fetchUnitDetail, fetchUnits, queryKeys } from '../../lib/api';
import { findContainingFeature, type PointLike } from '../../lib/geo';
import { useAppStore } from '../../lib/state';

/** The highlight colour for a selected unit — the same blue `ArtifactLayer`'s ✕ falls back to. */
export const SELECTED_UNIT_COLOR = '#2B6CB0';

export interface UnitLink {
  /** `intel.unit` — the unit currently under study, from either side of the link. */
  unit: string | null;
  /** Pane pick: adopt `unitId`, fit the map to it, and highlight it as a `units` result layer. */
  pick: (unitId: string, label?: string) => Promise<void>;
  /**
   * The other half of a pane pick: back to the whole programme. `#intelFocus`' "All
   * watersheds"/"All countries" option calls it — the pane's unit, the map's selection
   * and the highlight layer all go together, because a cleared select with a blue outline
   * still on the map is the "click did nothing" symptom read backwards.
   */
  clear: () => void;
}

export function useUnitLink(useCase: string): UnitLink {
  const qc = useQueryClient();
  const selectedUnit = useAppStore((s) => s.selectedUnit);
  const unit = useAppStore((s) => s.intel.unit);
  const setIntelUnit = useAppStore((s) => s.setIntelUnit);
  const setSelectedUnit = useAppStore((s) => s.setSelectedUnit);
  const addResultLayer = useAppStore((s) => s.addResultLayer);
  const removeResultLayer = useAppStore((s) => s.removeResultLayer);
  const fitBounds = useAppStore((s) => s.fitBounds);

  /**
   * The one place `intel:{uc}:selected` is built, so a map click and a pane pick agree.
   *
   * **An outline, never a fill** (client round 2 Oct 2026). It used to be a solid blue
   * polygon, which was invisible as a bug for as long as the only clickable thing on the
   * map was a choropleth it sat on top of — and it hid that choropleth's own class colour
   * even then. The moment a raster chip became clickable it painted a solid blue patch
   * over the very pixels the chip exists to show. An outline says "this one" over
   * anything: a class fill, a land-cover raster, or nothing at all.
   */
  const highlight = (unitId: string, label?: string) =>
    addResultLayer(
      {
        id: `intel:${useCase}:selected`,
        kind: 'units',
        label: label ?? unitId,
        unit_ids: [unitId],
        colors: { [unitId]: SELECTED_UNIT_COLOR },
        outlines: { [unitId]: SELECTED_UNIT_COLOR },
        outlineOnly: true,
      },
      { jobId: unitId, tool: useCase },
    );

  /**
   * Amendment 1 §0: watershed-or-country only. Resolves `id` (whatever
   * `HierarchyLayers`/`Choropleth`/`SearchBox` set `selectedUnit` to) to the watershed
   * containing the click point, else the unit's own country, else `id` itself unchanged —
   * a failed lookup (an unreachable hierarchy endpoint, a unit with neither geometry nor a
   * country on file) must still leave the reader with *something* selected rather than
   * nothing.
   */
  const resolveUnit = async (id: string): Promise<string> => {
    try {
      const detail = await qc.fetchQuery({ queryKey: queryKeys.unit(id), queryFn: () => fetchUnitDetail(id) });
      if (detail.unit_type === 'watershed' || (detail.unit_type === 'admin' && detail.admin_level === 0)) {
        return id;
      }
      const point: PointLike | null =
        useAppStore.getState().lastClickLatLng ??
        (detail.centroid ? ([detail.centroid[1], detail.centroid[0]] as [number, number]) : null);
      if (point) {
        const watersheds = await qc.fetchQuery({
          queryKey: queryKeys.units('watershed', null, 'ALL', 'z7', null),
          queryFn: () => fetchUnits('watershed', null, 'ALL', 'z7', null),
        });
        const hit = findContainingFeature(point, watersheds);
        if (hit) return hit.id;
      }
      if (detail.country) {
        const countries = await qc.fetchQuery({
          queryKey: queryKeys.units('admin', 0, 'ALL', 'z7', null),
          queryFn: () => fetchUnits('admin', 0, 'ALL', 'z7', null),
        });
        const match = countries.features.find((f) => f.properties.country === detail.country);
        if (match) return match.properties.id;
      }
      return id;
    } catch {
      // No detail for this id (a stale search result, an unreachable endpoint) — better an
      // unresolved but present selection than none at all.
      return id;
    }
  };

  // Map → pane: a boundary click landed in `selectedUnit` — resolve it to a watershed or a
  // country, adopt that as the pane's own unit and draw the highlight, but do not move the
  // view (the click already put it there).
  // Keyed off a ref rather than `unit` in the dependency array: `unit` is also written by
  // `pick` below, and re-running this effect off its own write would re-add a layer `pick`
  // already drew (harmless, since `addResultLayer` replaces in place by id, but pointless).
  const lastMapUnit = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedUnit || selectedUnit === lastMapUnit.current) return;
    lastMapUnit.current = selectedUnit;
    let cancelled = false;
    void resolveUnit(selectedUnit).then((resolved) => {
      if (cancelled) return;
      if (resolved !== selectedUnit) {
        // Stop this same effect from re-running off the correction it is about to make.
        lastMapUnit.current = resolved;
        setSelectedUnit(resolved);
      }
      if (resolved !== unit) setIntelUnit(resolved);
      highlight(resolved);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnit]);

  const pick = async (unitId: string, label?: string) => {
    setIntelUnit(unitId);
    setSelectedUnit(unitId);
    // Stop the map→pane effect above from re-running off this same write and clobbering
    // `label` with the bare unit id the moment `selectedUnit` changes underneath it.
    lastMapUnit.current = unitId;
    highlight(unitId, label);
    try {
      const detail = await qc.fetchQuery({
        queryKey: queryKeys.unit(unitId),
        queryFn: () => fetchUnitDetail(unitId),
      });
      if (detail.bbox) {
        const [minx, miny, maxx, maxy] = detail.bbox;
        fitBounds([
          [miny, minx],
          [maxy, maxx],
        ]);
      }
    } catch {
      // No geometry (or an unreachable hierarchy endpoint) for this unit — the highlight
      // still stands, only the automatic fit does not fire.
    }
  };

  /**
   * Back to the programme. `lastMapUnit` is reset as well, so re-picking the very unit
   * that was just cleared still re-runs the map→pane effect rather than being swallowed
   * as "the same selection as last time".
   */
  const clear = () => {
    lastMapUnit.current = null;
    setIntelUnit(null);
    setSelectedUnit(null);
    removeResultLayer(`intel:${useCase}:selected`);
  };

  return { unit, pick, clear };
}
